import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Search, HelpCircle } from "lucide-react";
import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

interface ValidatedFormFieldProps {
    id: string;
    label: string;
    placeholder?: string;
    type?: string;
    required?: boolean;
    value?: string;
    onChange?: (value: string) => void;
    showFetchButton?: boolean;
    onFetch?: () => void;
    showSelectButton?: boolean;
    onSelect?: () => void;
    selectButtonIcon?: React.ReactNode;
    selectButtonTooltip?: string;
    validator?: (value: string) => { isValid: boolean; errors: string[] };
    showErrors?: boolean;
    hint?: string;
    hintAriaLabel?: string;
}

function isNativeCapacitorApp() {
    if (typeof window === "undefined") return false;
    const cap = (window as Window & { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
    return Boolean(cap?.isNativePlatform?.());
}

const FieldHint = ({
    label,
    hint,
    ariaLabel,
}: {
    label: string;
    hint: string;
    ariaLabel?: string;
}) => {
    const [open, setOpen] = useState(false);
    const useSheet = isNativeCapacitorApp();
    const iconClassName =
        "inline-flex items-center justify-center rounded-full p-0.5 text-muted-foreground hover:text-foreground cursor-help touch-manipulation";

    if (useSheet) {
        return (
            <>
                <button
                    type="button"
                    className={iconClassName}
                    aria-label={ariaLabel || hint}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setOpen((current) => !current);
                    }}
                >
                    <HelpCircle className="h-3.5 w-3.5" />
                </button>
                {open &&
                    typeof document !== "undefined" &&
                    createPortal(
                        <div className="fixed inset-0 z-[200]">
                            <button
                                type="button"
                                className="absolute inset-0 bg-black/50"
                                aria-label="Close"
                                onClick={() => setOpen(false)}
                            />
                            <div
                                role="dialog"
                                aria-modal="true"
                                aria-labelledby="field-hint-title"
                                className="absolute inset-x-0 bottom-0 rounded-t-2xl border border-border bg-background p-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] shadow-lg"
                            >
                                <h2 id="field-hint-title" className="text-base font-semibold">
                                    {label}
                                </h2>
                                <p className="mt-2 text-sm text-muted-foreground">{hint}</p>
                            </div>
                        </div>,
                        document.body
                    )}
            </>
        );
    }

    return (
        <TooltipProvider delayDuration={200}>
            <Tooltip open={open} onOpenChange={setOpen}>
                <TooltipTrigger asChild>
                    <button
                        type="button"
                        className={iconClassName}
                        aria-label={ariaLabel || hint}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setOpen(true);
                        }}
                    >
                        <HelpCircle className="h-3.5 w-3.5" />
                    </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="z-[200] max-w-xs">
                    <p className="text-xs leading-relaxed">{hint}</p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
};

export const ValidatedFormField = ({
    id,
    label,
    placeholder,
    type = "text",
    required = false,
    value = "",
    onChange,
    showFetchButton = false,
    onFetch,
    showSelectButton = false,
    onSelect,
    selectButtonIcon,
    selectButtonTooltip = "Välj",
    validator,
    showErrors = true,
    hint,
    hintAriaLabel,
}: ValidatedFormFieldProps) => {
    const [errors, setErrors] = useState<string[]>([]);
    const [isTouched, setIsTouched] = useState(false);

    useEffect(() => {
        if (validator && (isTouched || value.length > 0)) {
            const validation = validator(value);
            if (JSON.stringify(validation.errors) !== JSON.stringify(errors)) {
                setErrors(validation.errors);
            }
        } else if (errors.length > 0) {
            setErrors([]);
        }
        // Only depend on value and isTouched, not errors to avoid infinite loops
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, isTouched, validator]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (onChange) {
            onChange(e.target.value);
        }
    };

    const handleBlur = () => {
        setIsTouched(true);
    };

    const hasError = errors.length > 0 && (isTouched || value.length > 0);

    return (
        <div className="space-y-2">
            <div className="flex items-center gap-1">
                <Label htmlFor={id}>
                    {label}
                    {required && <span className="text-red-500 ml-1">*</span>}
                </Label>
                {hint && (
                    <FieldHint
                        label={label}
                        hint={hint}
                        ariaLabel={hintAriaLabel}
                    />
                )}
            </div>
            <div className="flex gap-2">
                <Input
                    id={id}
                    type={type}
                    placeholder={placeholder}
                    value={value}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required={required}
                    className={`flex-1 ${hasError ? 'border-red-500' : ''}`}
                />
                {showFetchButton && (
                    <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={onFetch}
                        className="shrink-0 h-10 w-10"
                        title="Hämta uppgifter"
                    >
                        <Search className="h-4 w-4" />
                    </Button>
                )}
                {showSelectButton && (
                    <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={onSelect}
                        className="shrink-0 h-10 w-10"
                        title={selectButtonTooltip}
                    >
                        {selectButtonIcon || <Search className="h-4 w-4" />}
                    </Button>
                )}
            </div>
            {showErrors && hasError && (
                <div className="space-y-1">
                    {errors.map((error, index) => (
                        <p key={index} className="text-red-500 text-xs">{error}</p>
                    ))}
                </div>
            )}
        </div>
    );
};
